<template>
  <div class="container tyq-mobile-layout" :class="{ 'tyq-plugin-iframe': isPluginIframe }">
    <!-- 導航條（插件 iframe 隱藏） -->
    <div v-if="!isPluginIframe" class="nav-bar">
      <div class="nav-content">
        <div class="nav-left">
          <div class="logo-container">
            <div class="logo">
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path 
                  d="M4 18 L7 12 L10 16 L13 8 L16 14 L20 6" 
                  stroke="#FFD700"
                  stroke-width="2.5"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <span class="logo-text">Windata</span>
          </div>
          <div class="race-info">
            <div class="race-info-line">{{ currentRace ? `${currentRace.place} ${currentRace.game}` : '載入中...' }}</div>
            <div class="race-info-line">{{ currentRace ? formatDateTime(currentRace.date, currentRace.time) : '' }}</div>
          </div>
        </div>
        <div class="nav-right">
          <span v-if="appVersion" class="app-version">v{{ appVersion }}</span>
        </div>
      </div>
    </div>
   <div v-if="isPluginIframe && currentRace" class="plugin-session-bar">
     <span class="plugin-session-line">{{ formatDateTime(currentRace.date, currentRace.time) }}</span>
     <span class="plugin-session-line">{{ currentRace.place }} {{ currentRace.game }}</span>
   </div>
   <div v-if="isPluginIframe && pluginStatus" class="plugin-load-hint">
     {{ pluginStatus }}
   </div>
   <div class="main-pane" :class="{ 'main-pane-plugin': isPluginIframe }">
     <!-- 場次選擇器（插件 iframe 隱藏） -->
     <div v-if="!isPluginIframe" class="race-selector" style="display: flex; justify-content: space-between;">
      <div>
        <div class="race-numbers">
          <div v-for="race in races" 
            :key="race.uid"
            :class="['race-number', selectedRace === race.sn ? 'active' : '']"
            @click="selectRace(race.sn)"
          >
            {{ race.sn }}
          </div>
        </div>
      </div>  
      <div>
        <div class="race-numbers">  
          <div class="app-detail" :class="['race-number', showMore ? 'active' : '']" @click="viewMore">
             詳
          </div>
        </div>
      </div>
      <!-- 添加調試信息 -->
      <div class="debug-info" v-if="races.length === 0">
        無場次數據
      </div>
    </div>

    <!-- 表格部分 -->
    <div class="view-data-container">
      <div class="content-wrapper">
        <div class="table-wrapper">
          <HorseTable
            :data="data"
            :loading="raceDataLoading"
            :tableWidthValue="tableWidthValue"
            :showMore="showMore"
            :view-minute="tableViewMinute"
            :overnight-active="tableOvernightActive"
            :highlightPicks="highlightPicks"
            :highlightBankers="highlightBankers"
            :highlightColdMut00="highlightColdMut00"
            :highlight-hot-mut00="highlightHotMut00"
            :highlight-warm-mut00="highlightWarmMut00"
            :spectrum-sort-by="tableSortMode"
            @sort-change="onSpectrumSortChange"
          />
        </div>
      </div>
    </div>
    <!-- 圖表部分 -->
    <div
      class="chart-data-container"
      :class="{ 'chart-data-container--mock-live': showMockLivePanel }"
    >
      <div v-if="showMockLivePanel" class="tyq-mock-live-panel">
        <div class="tyq-mock-live-title">测试直播 API · seq {{ testLiveSession.seq }} · entry {{ testEntryDisplay }} · current {{ mockSyncDisplay }} · view {{ mockViewDisplay }}</div>
        <div class="tyq-mock-live-btns">
          <button type="button" @click="mockSimulateRefresh(false)">WS刷新</button>
          <button type="button" @click="mockSimulateRefresh(true)">缘左移</button>
          <button type="button" @click="mockSnapLiveEdge">贴直播缘</button>
          <button type="button" @click="mockToggleAuto">{{ mockAutoRunning ? '停自动' : '自动5s' }}</button>
        </div>
      </div>
      <div class="content-wrapper">
        <div class="chart-wrapper">
          <HorseChart
            ref="horseChartRef"
            :data="data"
            :current_minute="current_minute"
            :entry_minute="entry_minute"
            :time_points="time_points"
            :view_source="view_source"
            :race_completed="raceCompleted"
            :race-id="currentRace?.uid || ''"
            :test-live-boot="mockLiveActive"
            :plugin-embed="isPluginIframe"
            :compact-layout="true"
            :highlight-picks="highlightPicks"
            :highlight-bankers="highlightBankers"
            :highlight-cold-mut00="highlightColdMut00"
            :highlight-hot-mut00="highlightHotMut00"
            :highlight-warm-mut00="highlightWarmMut00"
            :spectrum-sort-by="chartSortMode"
            @view-minute-change="onChartViewMinuteChange"
          />
        </div>
      </div>
    </div>
   </div>
  </div>
</template>

<script>
import { ref, computed, reactive, onMounted, onUnmounted, nextTick } from 'vue';
import axios from 'axios';
import HorseTable from '../components/HorseTable.vue';
import HorseChart from '../components/HorseChart.vue';
import {
  reconcileCurrentMinute,
  isRaceSessionEnded,
  inferSpectrumDisplayEdge,
  inferSpectrumVisualEdge,
  inferSpectrumReachableActivityEdge,
  inferRaceViewXSyncMinute,
  minViewMinute,
  collectAvailableTimePoints,
  normalizeRaceViewXTimePoints,
} from './timelineLogic.js';
import { timelineDebug, printTimelineDebugHint } from './timelineDebug.js';
import {
  SPECTRUM_SORT_WIN_R18,
  SPECTRUM_SORT_WIN,
} from './spectrumSort.js';
import { collectSuddenMutationBuckets } from './suddenMutation.js';
import {
  isMockLiveEnabled,
  getMockLiveAutoMs,
  createTestLiveSession,
  buildTestLiveGetviewUrl,
  absorbTestLiveResponse,
  printMockLiveHint,
  logMockLive,
  clearStaleMockLiveFlag,
} from './timelineMockLive.js';
// import OddsDisplay from '../components/OddsDisplay.vue'

export default {
  name: 'ViewData',
  components: {
    HorseTable,
    HorseChart,
    // OddsDisplay
  },
  setup() {
    const data = ref([]);
    const current_minute = ref(-1);
    /** 首次进入本场时记录的最左刻度，刷新不再改 */
    const entry_minute = ref(null);
    /** 柱图滑块刻度（API time_points） */
    const time_points = ref([]);
    const view_source = ref('live');
    const selectedRace = ref(1);
    const races = ref([]);
    const currentGame = ref('');
    const currentRace = ref(null);
    /** getview 返回的 race_completed，优先于本地时间推算 */
    const apiRaceCompleted = ref(false);
    /** 防止异步 getview 乱序：旧场次响应不得覆盖新场次 */
    let fetchRaceSeq = 0;
    /** 直播轮询序号（与切场 fetchRaceSeq 分离，避免互相丢弃） */
    let refreshSeq = 0;
    let pluginBootstrapTimer1 = null;
    let pluginBootstrapTimer2 = null;
    const raceDataLoading = ref(false);
    const getviewCacheByUid = {};
    const getviewInflightByUid = {};
    const GETVIEW_CACHE_MS = 5000;
    /** 本场已做过入场 MIN(min)；换场清除 */
    const raceTimelineBooted = new Set();

    // 添加获取 URL 参数的函数
    const getUrlParam = (name) => {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(name);
    };

    const isPluginIframe = computed(() => {
      return getUrlParam('embed') === '1' || getUrlParam('plugin') === '1';
    });

    /** 柱图区：滑块固定高度，柱图高度随视口动态计算 */
    const MOBILE_SLIDER_H = 40;
    const PLUGIN_SLIDER_H = 34;
    /** 孖上+孖下两行固定高度（各行 17px + 2px 间距） */
    const PLUGIN_DB_BUBBLE_H = 36;
    /** 柱图轴标（马号/赔率）与孖上泡泡行之间的留白 */
    const PLUGIN_CHART_BUBBLE_GAP = 1;
    const MOBILE_CHART_MIN = 140;
    const MOBILE_CHART_MAX = 360;
    const PLUGIN_CHART_MIN = 120;
    /** 模拟直播调试条高度（嵌入柱图区顶部，不挡时间轴） */
    const MOCK_LIVE_PANEL_H = 56;
    /** 插件浮窗柱图高度缩放（减 20%） */
    const PLUGIN_CHART_HEIGHT_SCALE = 0.8;

    const measureSpectrumContentHeight = () => {
      const table = document.querySelector('.view-data-container .custom-table')
        || document.querySelector('.view-data-container .table-container');
      if (!table) return 0;
      return Math.ceil(table.scrollHeight || table.offsetHeight || 0);
    };

    const getViewportSize = () => {
      const vv = window.visualViewport;
      return {
        w: Math.round((vv && vv.width) || window.innerWidth || 390),
        h: Math.round((vv && vv.height) || window.innerHeight || 700),
      };
    };

    /** 本场是否已结束（可回看最早分钟）；非「后面还有第N场」 */
    const raceCompleted = computed(() => {
      if (mockLiveActive.value) return false;
      if (apiRaceCompleted.value) return true;
      if (isRaceSessionEnded(currentRace.value, view_source.value)) return true;
      if (view_source.value === 'historical') return true;
      return false;
    });

    const mockLiveActive = ref(isMockLiveEnabled());
    const showMockLivePanel = computed(() => mockLiveActive.value && !!currentRace.value?.uid);
    const testLiveSession = reactive(createTestLiveSession());
    let mockAutoTimer = null;
    const mockAutoRunning = ref(false);

    const testEntryDisplay = computed(() => {
      const v = testLiveSession.entryMin;
      return v != null && !Number.isNaN(Number(v)) ? v : '—';
    });
    const mockSyncDisplay = computed(() => {
      const v = current_minute.value;
      return v != null && !Number.isNaN(Number(v)) ? v : '—';
    });
    const mockViewDisplay = computed(() => {
      const snap = horseChartRef.value?.getTimelineDebugSnapshot?.();
      return snap?.view_minute != null ? snap.view_minute : tableViewMinute.value;
    });

    const resetTestLiveSession = () => {
      testLiveSession.seq = 0;
      testLiveSession.entryMin = null;
    };

    const mockSnapLiveEdge = () => {
      horseChartRef.value?.goToLiveFollowEdge?.();
      logMockLive('snap_live', horseChartRef.value?.getTimelineDebugSnapshot?.());
    };

    const mockSimulateRefresh = async (advanceSeq) => {
      if (!currentRace.value?.uid) return;
      await fetchCurrentRaceData({ testLive: true, advanceSeq: !!advanceSeq });
    };

    const     mockToggleAuto = () => {
      if (mockAutoRunning.value) {
        if (mockAutoTimer) clearInterval(mockAutoTimer);
        mockAutoTimer = null;
        mockAutoRunning.value = false;
        logMockLive('auto_stop', {});
        return;
      }
      horseChartRef.value?.goToLiveFollowEdge?.();
      const ms = getMockLiveAutoMs() || 5000;
      mockAutoTimer = setInterval(() => {
        mockSimulateRefresh(true);
      }, ms);
      mockAutoRunning.value = true;
      logMockLive('auto_start', { ms });
    };

    const stopMockAuto = () => {
      if (mockAutoTimer) clearInterval(mockAutoTimer);
      mockAutoTimer = null;
      mockAutoRunning.value = false;
    };

    const buildGetviewUrl = (raceId, opts = {}) => {
      const rid = String(raceId || '');
      if (mockLiveActive.value) {
        return buildTestLiveGetviewUrl(rid, testLiveSession, {
          advance: !!opts.advanceSeq,
        });
      }
      return `https://api.windata.top/getview?race_id=${rid}`;
    };

    const afterTestLiveFetch = (payload) => {
      if (!mockLiveActive.value || !payload || payload.code === -1) return;
      if (!payload.test_mode) {
        console.warn('[tyq] 未收到 test_mode，请确认 API 已部署 getview_test');
      }
      absorbTestLiveResponse(testLiveSession, payload);
      logMockLive('api', {
        test_seq: testLiveSession.seq,
        test_entry_min: testLiveSession.entryMin,
        current_minute: payload.current_minute,
        test_current_db_min: payload.test_current_db_min,
      });
    };

    const highlightPicks = ref([]);
    const highlightBankers = ref([]);
    const highlightColdMut00 = ref([]);
    const highlightWarmMut00 = ref([]);
    const highlightHotMut00 = ref([]);
    /** 本地推断的 00突冷马；插件 postMessage 可覆盖 cold，暖/热始终跟数据 */
    const localColdMut00 = ref([]);
    /** 回测 iframe：URL / postMessage 传入的选码，fetch 完成后再次应用 */
    const tyqHighlightPending = ref(null);

    /** 从当前 horses 推断 00突 Top6 热/温/冷（手机无插件 postMessage 时也能着色） */
    const refreshSuddenMutationHighlights = (horses) => {
      const buckets = collectSuddenMutationBuckets(horses || data.value || []);
      localColdMut00.value = buckets.cold.slice();
      highlightWarmMut00.value = buckets.warm.slice();
      highlightHotMut00.value = buckets.hot.slice();
      const fromPlugin = tyqHighlightPending.value?.cold_mut00;
      const pluginCold = Array.isArray(fromPlugin)
        ? fromPlugin.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
        : [];
      highlightColdMut00.value = pluginCold.length ? pluginCold : localColdMut00.value.slice();
    };

    const applyViewPayload = async (payload, expectedRaceId, opts = {}) => {
      const payloadRid = payload?.race_id != null ? String(payload.race_id) : '';
      const ridKey = expectedRaceId != null ? String(expectedRaceId) : payloadRid;
      const firstBoot = ridKey && !raceTimelineBooted.has(ridKey);
      const wasAtLiveEdge = !firstBoot
        && !raceCompleted.value
        && horseChartRef.value?.isTimelineAtLiveFollowEdge?.();
      if (expectedRaceId != null && payloadRid && payloadRid !== String(expectedRaceId)) {
        timelineDebug('skip_stale_payload', { expected: expectedRaceId, got: payloadRid });
        return false;
      }
      const horses = payload?.horses || [];
      const apiRaw = payload?.current_minute ?? -1;
      const src = payload?.view_source === 'historical' ? 'historical' : 'live';
      const historicalUrl = getUrlParam('historical') === '1';
      if (payload && (payload.race_id || payload.date || payload.time)) {
        currentRace.value = {
          ...(currentRace.value || {}),
          uid: payload.race_id ?? currentRace.value?.uid,
          date: payload.date ?? currentRace.value?.date,
          time: payload.time ?? currentRace.value?.time,
          sn: payload.sn ?? currentRace.value?.sn,
          game: payload.game ?? currentRace.value?.game,
          venue: payload.venue ?? currentRace.value?.venue,
          place: payload.place ?? currentRace.value?.place,
        };
      }
      apiRaceCompleted.value = payload?.race_completed === true
        || payload?.race_completed === 1
        || payload?.race_completed === '1';
      view_source.value = historicalUrl ? 'historical' : src;
      time_points.value = normalizeRaceViewXTimePoints(
        Array.isArray(payload?.time_points)
          ? payload.time_points.map((p) => Number(p))
          : (horses.length ? collectAvailableTimePoints(horses) : []),
      );
      const reconciled = reconcileCurrentMinute(apiRaw, horses, {
        viewSource: view_source.value,
        historical: historicalUrl,
        raceCompleted: raceCompleted.value,
      });
      if (firstBoot) {
        entry_minute.value = time_points.value.length
          ? minViewMinute(time_points.value)
          : Number(reconciled);
      }
      current_minute.value = reconciled;
      data.value = horses;
      refreshSuddenMutationHighlights(horses);
      timelineDebug('getview', {
        race_id: currentRace.value?.uid,
        sn: currentRace.value?.sn,
        api_current_minute: apiRaw,
        entry_minute: entry_minute.value,
        reconciled_current_minute: reconciled,
        view_source: view_source.value,
        race_completed: raceCompleted.value,
        api_race_completed: apiRaceCompleted.value,
        spectrum_edge: inferSpectrumDisplayEdge(horses),
        spectrum_visual: inferSpectrumVisualEdge(horses),
        reachable_edge: inferSpectrumReachableActivityEdge(horses),
        race_view_x_sync: inferRaceViewXSyncMinute(horses),
        time_points: time_points.value,
        horses: horses.length,
        is_refresh: !firstBoot,
        first_boot: firstBoot,
        was_at_live_edge: wasAtLiveEdge,
      });
      await nextTick();
      updateMobileLayout();
      if (isPluginIframe.value) {
        setTimeout(updateMobileLayout, 60);
      }
      if (!opts.skipTimeline) {
        if (raceCompleted.value) {
          if (firstBoot) {
            await nextTick();
            horseChartRef.value?.applyCompletedRaceView?.();
          }
          if (ridKey) raceTimelineBooted.add(ridKey);
        } else if (firstBoot) {
          await nextTick();
          if (mockLiveActive.value || payload?.test_mode) {
            horseChartRef.value?.applyTestLiveEntry?.();
          } else {
            horseChartRef.value?.applyRaceEntry?.();
          }
          if (ridKey) raceTimelineBooted.add(ridKey);
        } else {
          horseChartRef.value?.applyLiveRefresh?.({ wasAtLiveEdge });
        }
      }
      await nextTick();
      horseChartRef.value?.refreshChart?.();
      return true;
    };

    let tableWidthValue = ref(0)
    let tableHeightValue = ref(0)
    const horseChartRef = ref(null)
    const pluginStatus = ref('')
    const pluginBodyHeight = ref(0)
    const tableViewMinute = ref(-1)
    const tableOvernightActive = ref(false)
    /** null=默认（表独前/柱图独赢）；点击表头后表+柱图同步 */
    const userSpectrumSortMode = ref(null)
    const tableSortMode = computed(() => userSpectrumSortMode.value || SPECTRUM_SORT_WIN_R18)
    const chartSortMode = computed(() => userSpectrumSortMode.value || SPECTRUM_SORT_WIN_R18)
    const onSpectrumSortChange = (mode) => {
      if (mode === SPECTRUM_SORT_WIN_R18 || mode === SPECTRUM_SORT_WIN) {
        userSpectrumSortMode.value = mode
      }
    }
    const appVersion = ref('')
    // 添加 WebSocket 連接
    const websocket = ref(null);

    /** 频谱表 + 柱图：按可用高度动态分配（插件 iframe 同步父窗尺寸） */
    const updateMobileLayout = () => {
      const root = document.querySelector('.container.tyq-mobile-layout');
      const mainPane = document.querySelector('.main-pane');
      const spectrumEl = document.querySelector('.view-data-container');
      const sessionBar = document.querySelector('.plugin-session-bar');
      const hintEl = document.querySelector('.plugin-load-hint');
      const raceSel = document.querySelector('.race-selector');
      const chartBlockEl = document.querySelector('.chart-data-container');
      const vp = getViewportSize();

      if (isPluginIframe.value && pluginBodyHeight.value > 0) {
        if (root) root.style.height = `${pluginBodyHeight.value}px`;
      } else if (root) {
        root.style.height = `${vp.h}px`;
      }

      let paneH = mainPane?.clientHeight || 0;
      if (paneH < 80) {
        if (isPluginIframe.value && pluginBodyHeight.value > 0) {
          const overhead = (sessionBar?.offsetHeight || 0) + (hintEl?.offsetHeight || 0);
          paneH = pluginBodyHeight.value - overhead;
        } else {
          const navH = 32;
          const raceH = raceSel?.offsetHeight || 36;
          paneH = vp.h - navH - raceH;
        }
      }

      const sliderH = isPluginIframe.value ? PLUGIN_SLIDER_H : MOBILE_SLIDER_H;
      /** 浏览器移动端 compactLayout 也显示孖上/孖下，须预留高度 */
      const bubbleH = PLUGIN_DB_BUBBLE_H;
      const chartBubbleGap = PLUGIN_CHART_BUBBLE_GAP;
      const mockPanelH = showMockLivePanel.value ? MOCK_LIVE_PANEL_H : 0;
      const chartMin = isPluginIframe.value ? PLUGIN_CHART_MIN : MOBILE_CHART_MIN;
      const chartRatio = isPluginIframe.value ? 0.42 : 0.34;
      let chartH = Math.round(paneH * chartRatio);
      chartH = Math.max(chartMin, Math.min(MOBILE_CHART_MAX, chartH));
      let blockH = chartH + sliderH + bubbleH + chartBubbleGap + mockPanelH;
      let spectrumMax = Math.max(100, paneH - blockH);
      let spectrumH = spectrumMax;

      if (isPluginIframe.value && paneH > 0) {
        if (spectrumEl) {
          spectrumEl.style.height = 'auto';
          spectrumEl.style.maxHeight = 'none';
          spectrumEl.style.overflowY = 'visible';
        }
        const contentH = measureSpectrumContentHeight();
        spectrumH = contentH > 0 ? contentH : 72;
        chartH = paneH - spectrumH - sliderH - bubbleH - chartBubbleGap - mockPanelH;
        chartH = Math.round(chartH * PLUGIN_CHART_HEIGHT_SCALE);
        chartH = Math.max(
          Math.round(chartMin * PLUGIN_CHART_HEIGHT_SCALE),
          Math.min(MOBILE_CHART_MAX, chartH),
        );
        blockH = chartH + sliderH + bubbleH + chartBubbleGap + mockPanelH;
        spectrumMax = spectrumH;
      }

      if (root) {
        root.style.setProperty('--tyq-chart-h', `${chartH}px`);
        root.style.setProperty('--tyq-chart-bubble-gap', `${chartBubbleGap}px`);
        root.style.setProperty('--tyq-block-h', `${blockH}px`);
        root.style.setProperty('--tyq-spectrum-max', `${spectrumMax}px`);
        root.style.setProperty('--tyq-spectrum-h', `${spectrumH}px`);
        root.style.setProperty('--tyq-slider-h', `${sliderH}px`);
        root.style.setProperty('--tyq-db-bubble-h', `${bubbleH}px`);
        root.style.setProperty('--tyq-mock-panel-h', `${mockPanelH}px`);
      }

      if (spectrumEl && paneH > 0) {
        if (isPluginIframe.value) {
          spectrumEl.style.flex = '0 0 auto';
          spectrumEl.style.minHeight = '0';
          spectrumEl.style.maxHeight = 'none';
          spectrumEl.style.height = 'auto';
          spectrumEl.style.overflowY = 'visible';
        } else {
          spectrumEl.style.flex = '1 1 0';
          spectrumEl.style.minHeight = '80px';
          spectrumEl.style.height = '';
          spectrumEl.style.setProperty('max-height', `${spectrumMax}px`, 'important');
          spectrumEl.style.setProperty('overflow-y', 'auto', 'important');
          spectrumEl.style.setProperty('overflow-x', 'hidden', 'important');
        }
        if (isPluginIframe.value) {
          spectrumEl.style.overflowX = 'hidden';
        }
      }
      if (chartBlockEl) {
        if (isPluginIframe.value) {
          chartBlockEl.style.flex = '0 0 auto';
          chartBlockEl.style.height = `${blockH}px`;
          chartBlockEl.style.minHeight = `${blockH}px`;
          chartBlockEl.style.maxHeight = `${blockH}px`;
        } else {
          chartBlockEl.style.flex = '0 0 auto';
          chartBlockEl.style.height = `${blockH}px`;
          chartBlockEl.style.minHeight = `${blockH}px`;
          chartBlockEl.style.maxHeight = `${blockH}px`;
        }
      }

      const tableW = isPluginIframe.value
        ? (spectrumEl?.clientWidth || root?.clientWidth || vp.w)
        : vp.w;
      tableWidthValue.value = Math.max(Math.round(tableW), 320);

      const applyChartHeight = () => {
        const chartEl = document.querySelector('.chart-data-container .echarts-chart');
        if (chartEl) {
          chartEl.setAttribute('data-plugin-max-h', String(chartH));
          chartEl.style.height = `${chartH}px`;
          chartEl.style.flex = '0 0 auto';
        }
        timelineDebug('layout', {
          mode: isPluginIframe.value ? 'plugin' : 'mobile',
          tableWidth: tableWidthValue.value,
          paneH,
          chartBlockH: blockH,
          chartH,
          spectrumMax,
          spectrumH,
          spectrumContent: measureSpectrumContentHeight(),
          viewport: vp,
        });
        horseChartRef.value?.refreshChart?.();
      };
      requestAnimationFrame(() => requestAnimationFrame(applyChartHeight));
    };

    const handleResize = () => updateMobileLayout();

    //  // 監聽 tableWidthValue 的變化
    //  watch(tableWidthValue, (newValue) => {
    //   console.log('tableWidthValue changed:', newValue);
    // });

    const fetchRaces = async () => {
      try {
        //console.log('開始獲取場次數據');
        const response = await axios.get('https://api.windata.top/getrace');
        //console.log('獲取到的場次數據:', response.data);
        
        // 根據API返回格式正確解析數據
        if (response.data && response.data.code === 0 && Array.isArray(response.data.race)) {
          races.value = response.data.race;
          console.log('場次數據已設置，長度:', races.value.length);
        } else {
          console.warn('API返回的場次數據格式不正確:', response.data);
          races.value = [];
        }
        
        // 強制更新DOM
        await nextTick();
        
        if (races.value.length > 0) {
          const urlRaceId = getUrlParam('race_id');
          console.log('URL race_id:', urlRaceId);

          if (urlRaceId) {
            const targetRace = races.value.find(race => String(race.uid) === String(urlRaceId));
            if (targetRace) {
              selectedRace.value = targetRace.sn;
              currentRace.value = targetRace;
              await fetchRaceData(targetRace.uid);
              return;
            }
            console.warn('未找到URL指定的場次:', urlRaceId);
            await fetchRaceData(urlRaceId);
            return;
          }

          if (isPluginIframe.value) {
            pluginStatus.value = '等待場次…';
            schedulePluginBootstrap();
            return;
          }

          const firstRace = races.value.find(race => race.sn === 1);
          if (firstRace) {
            //console.log('選擇第一場比賽:', firstRace);
            selectedRace.value = firstRace.sn;
            currentRace.value = firstRace;
            await fetchRaceData(firstRace.uid);
          } else {
            console.warn('未找到場次號為1的比赛');
            if (races.value[0]) {
             // console.log('選擇第一個可用比賽:', races.value[0]);
              selectedRace.value = races.value[0].sn;
              currentRace.value = races.value[0];
              await fetchRaceData(races.value[0].uid);
            }
          }
        } else {
          console.warn('未獲取到任何場次數據');
        }
      } catch (error) {
        console.error('獲取場次數據失敗:', error);
      }
    };

    const cancelPluginBootstrap = () => {
      if (pluginBootstrapTimer1) {
        clearTimeout(pluginBootstrapTimer1);
        pluginBootstrapTimer1 = null;
      }
      if (pluginBootstrapTimer2) {
        clearTimeout(pluginBootstrapTimer2);
        pluginBootstrapTimer2 = null;
      }
    };

    const schedulePluginBootstrap = () => {
      cancelPluginBootstrap();
      pluginBootstrapTimer1 = setTimeout(() => pluginBootstrapRace(), 300);
      pluginBootstrapTimer2 = setTimeout(() => pluginBootstrapRace(), 1200);
    };

    /** 插件 iframe 首次无 URL race_id 时补载；禁止自动跳到「有数据的其它场」 */
    const pluginBootstrapRace = async () => {
      if (!isPluginIframe.value || data.value.length) {
        if (data.value.length) pluginStatus.value = '';
        return;
      }
      const rid = getUrlParam('race_id');
      if (!rid) return;
      pluginStatus.value = '載入場次…';
      await loadRacesForRaceId(rid);
      if (data.value.length) {
        pluginStatus.value = '';
        updateMobileLayout();
        return;
      }
      pluginStatus.value = '該場無數據';
    };

    const fetchRaceData = async (raceId) => {
      const rid = String(raceId || '');
      if (!rid) return false;
      const seq = ++fetchRaceSeq;
      const now = Date.now();
      const cached = getviewCacheByUid[rid];
      if (cached && now - cached.at < GETVIEW_CACHE_MS) {
        if (seq !== fetchRaceSeq) return false;
        const applied = await applyViewPayload(cached.json, rid);
        if (applied && isPluginIframe.value) pluginStatus.value = '';
        raceDataLoading.value = false;
        return applied;
      }
      if (getviewInflightByUid[rid]) {
        try {
          const json = await getviewInflightByUid[rid];
          if (seq !== fetchRaceSeq) return false;
          if (json && json.code !== -1) {
            const applied = await applyViewPayload(json, rid);
            if (applied && isPluginIframe.value) pluginStatus.value = '';
            raceDataLoading.value = false;
            return applied;
          }
        } catch (e) { /* fall through */ }
      }
      try {
        apiRaceCompleted.value = false;
        raceDataLoading.value = true;
        if (!data.value.length) {
          horseChartRef.value?.resetTimelineForRace?.();
          raceTimelineBooted.delete(rid);
          resetTestLiveSession();
          current_minute.value = -1;
          entry_minute.value = null;
          time_points.value = [];
        }
        const urlHighlight = readTyqHighlightFromUrl();
        if (urlHighlight) {
          tyqHighlightPending.value = mergeTyqHighlightConfig(tyqHighlightPending.value, urlHighlight);
        }
        if (isPluginIframe.value) pluginStatus.value = '載入中…';
        const fetchTask = axios.get(buildGetviewUrl(rid));
        getviewInflightByUid[rid] = fetchTask.then((r) => r.data);
        const response = await fetchTask;
        delete getviewInflightByUid[rid];
        if (seq !== fetchRaceSeq) {
          timelineDebug('skip_stale_fetch', { rid, seq, latest: fetchRaceSeq });
          raceDataLoading.value = false;
          return false;
        }

        if (response.data.code === -1) {
          console.warn('API返回錯誤碼 -1');
          data.value = [];
          current_minute.value = -1;
          entry_minute.value = null;
          time_points.value = [];
          if (isPluginIframe.value) pluginStatus.value = '該場無數據';
        } else {
          getviewCacheByUid[rid] = { json: response.data, at: Date.now() };
          afterTestLiveFetch(response.data);
          const applied = await applyViewPayload(response.data, rid);
          if (!applied) {
            raceDataLoading.value = false;
            return false;
          }
          if (isPluginIframe.value) pluginStatus.value = '';
        }

        await nextTick();
        updateMobileLayout();
        if (isPluginIframe.value && (tyqHighlightPending.value || readTyqHighlightFromUrl())) {
          flushTyqHighlightAfterLoad();
          setTimeout(flushTyqHighlightAfterLoad, 120);
        }
        raceDataLoading.value = false;
        return true;
      } catch (error) {
        delete getviewInflightByUid[rid];
        raceDataLoading.value = false;
        if (seq === fetchRaceSeq) {
          console.error('獲取比賽數據失敗:', error);
          if (isPluginIframe.value) pluginStatus.value = '載入失敗';
        }
        return false;
      }
    };

    const selectRace = (raceNumber) => {
      console.log('選擇場次:', raceNumber);
      selectedRace.value = raceNumber;
      const selectedRaceData = races.value.find(race => race.sn === raceNumber);
      if (selectedRaceData) {
        console.log('找到對應場次數據:', selectedRaceData);
        data.value = [];
        current_minute.value = -1;
        currentRace.value = selectedRaceData;
        fetchRaceData(selectedRaceData.uid);
      } else {
        console.warn('未找到場次號為', raceNumber, '的比赛');
      }
    };

    const formatDateTime = (date, time) => {
      if (!date || !time) return '';
      return `${date}(${time})`;
    };

    const fetchRaceListOnly = async () => {
      try {
        const response = await axios.get('https://api.windata.top/getrace');
        if (response.data?.code === 0 && Array.isArray(response.data.race)) {
          races.value = response.data.race;
        }
      } catch (e) {
        console.error('獲取場次列表失敗:', e);
      }
    };

    const loadRacesForRaceId = async (raceId, meetingDate) => {
      if (!raceId) return;
      const rid = String(raceId);
      cancelPluginBootstrap();
      let targetRace = races.value.find((r) => String(r.uid) === rid);
      if (!targetRace && !races.value.length) {
        await fetchRaceListOnly();
        targetRace = races.value.find((r) => String(r.uid) === rid);
      }
      if (!targetRace && meetingDate && !races.value.length) {
        await fetchRaceListOnly();
        targetRace = races.value.find((r) => String(r.uid) === rid);
      }
      if (targetRace) {
        selectedRace.value = targetRace.sn;
        currentRace.value = targetRace;
      } else if (!currentRace.value || String(currentRace.value.uid) !== rid) {
        currentRace.value = {
          ...(currentRace.value || {}),
          uid: rid,
        };
      }
      await fetchRaceData(rid);
    };

    const normPickNums = (arr) => (Array.isArray(arr) ? arr : String(arr || '').split(','))
      .map((n) => Number(n))
      .filter((n) => Number.isFinite(n) && n > 0);

    const parseCsvPickParam = (raw) => {
      if (raw == null || raw === '') return [];
      return normPickNums(String(raw).split(','));
    };

    const readTyqHighlightFromUrl = () => {
      const picks = parseCsvPickParam(getUrlParam('picks'));
      const bankers = parseCsvPickParam(getUrlParam('bankers'));
      const fmRaw = getUrlParam('focus_minute');
      const focusMinute = fmRaw != null && fmRaw !== '' ? Number(fmRaw) : null;
      if (!picks.length && !bankers.length && !Number.isFinite(focusMinute)) return null;
      return {
        picks,
        bankers,
        focus_minute: Number.isFinite(focusMinute) ? focusMinute : null,
      };
    };

    const mergeTyqHighlightConfig = (base, patch) => {
      const out = { ...(base || {}) };
      if (Array.isArray(patch?.picks)) out.picks = patch.picks;
      if (Array.isArray(patch?.bankers)) out.bankers = patch.bankers;
      if (Array.isArray(patch?.cold_mut00)) out.cold_mut00 = patch.cold_mut00;
      if (patch?.focus_minute != null && patch.focus_minute !== '') {
        const fm = Number(patch.focus_minute);
        if (Number.isFinite(fm)) out.focus_minute = fm;
      }
      return out;
    };

    const applyTyqFocusMinute = (minute) => {
      const m = Number(minute);
      if (!Number.isFinite(m)) return;
      const chart = horseChartRef.value;
      if (!chart?.applyUserViewMinute) return;
      chart.applyUserViewMinute(m);
    };

    const applyTyqHighlightState = (cfg, opts = {}) => {
      if (!cfg) return;
      highlightPicks.value = normPickNums(cfg.picks);
      highlightBankers.value = normPickNums(cfg.bankers);
      const pluginCold = normPickNums(cfg.cold_mut00);
      highlightColdMut00.value = pluginCold.length ? pluginCold : localColdMut00.value.slice();
      const applyFocus = opts.applyFocus !== false && cfg.focus_minute != null;
      nextTick(() => {
        if (applyFocus && data.value.length) {
          applyTyqFocusMinute(cfg.focus_minute);
        }
        horseChartRef.value?.refreshChart?.();
      });
    };

    const flushTyqHighlightAfterLoad = () => {
      const cfg = tyqHighlightPending.value || readTyqHighlightFromUrl();
      if (!cfg) return;
      applyTyqHighlightState(cfg);
    };

    const applyTyqHighlight = (msg) => {
      const patch = {
        picks: normPickNums(msg?.picks),
        bankers: normPickNums(msg?.bankers),
        cold_mut00: normPickNums(msg?.cold_mut00),
        focus_minute: msg?.focus_minute,
      };
      tyqHighlightPending.value = mergeTyqHighlightConfig(
        tyqHighlightPending.value || readTyqHighlightFromUrl(),
        patch,
      );
      applyTyqHighlightState(tyqHighlightPending.value);
    };

    const onChartViewMinuteChange = (payload) => {
      if (!payload || typeof payload !== 'object') return;
      tableViewMinute.value = Number(payload.view_minute);
      tableOvernightActive.value = !!payload.isOvernightActive;
    };

    const onPluginMessage = (ev) => {
      if (window.parent === window) return;
      const msg = ev.data;
      if (!msg || typeof msg !== 'object' || !msg.type) return;
      if (!String(msg.type).startsWith('windata_tyq_')) return;
      if (msg.type === 'windata_tyq_highlight') {
        applyTyqHighlight(msg);
        return;
      }
      if (msg.type === 'windata_tyq_set_race') {
        cancelPluginBootstrap();
        const rid = String(msg.race_id || '');
        if (!rid) return;
        const targetRace = races.value.find((r) => String(r.uid) === rid);
        if (targetRace) {
          selectedRace.value = targetRace.sn;
          currentRace.value = targetRace;
        } else if (!currentRace.value || String(currentRace.value.uid) !== rid) {
          currentRace.value = { ...(currentRace.value || {}), uid: rid };
        }
        fetchRaceData(rid).then(() => {
          nextTick(() => updateMobileLayout());
        });
        return;
      }
      if (msg.type === 'windata_tyq_refresh') {
        if (currentRace.value) fetchCurrentRaceData();
        return;
      }
      if (msg.type === 'windata_tyq_iframe_size') {
        const h = Number(msg.height);
        const w = Number(msg.width);
        if (h > 80) pluginBodyHeight.value = h;
        if (w > 80) tableWidthValue.value = Math.round(w);
        nextTick(() => setTimeout(updateMobileLayout, 50));
      }
    };

    const publishTyqVersion = async () => {
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`);
        const json = await res.json();
        if (json?.version) {
          appVersion.value = String(json.version);
          if (window.parent !== window) {
            window.parent.postMessage({
              type: 'windata_tyq_version',
              version: appVersion.value,
              build: json.build || '',
            }, '*');
          }
        }
      } catch (e) { /* ignore */ }
    };

    const connectWebSocket = () => {
      if (isPluginIframe.value) return;
      if (websocket.value) {
        try {
          websocket.value.onclose = null;
          websocket.value.close();
        } catch (e) { /* ignore */ }
        websocket.value = null;
      }
      websocket.value = new WebSocket('wss://wss.windata.top:8788');

      websocket.value.onopen = () => {
        console.log('WebSocket 連接已建立');
        websocket.value.send(JSON.stringify({ type: "windata_login" }));
      };

      websocket.value.onmessage = (event) => {
        const message = JSON.parse(event.data);
        //console.log('收到WebSocket消息:', message);
        
        // 檢查消息類型並回應
        if (message.type === 'ping') {
          websocket.value.send(JSON.stringify({ type: 'pong' }));
          //console.log('回應ping消息');
        }

        // refresh：有 race_id 则匹配当前场；无 race_id（旧 pushnotice）则刷新当前场
        if (message.type === 'refresh') {
          if (!currentRace.value?.uid) return;
          const msgRid = message.race_id != null ? String(message.race_id) : '';
          const curRid = String(currentRace.value.uid);
          if (!msgRid || msgRid === curRid) {
            fetchCurrentRaceData();
          }
        }
      };

      websocket.value.onclose = () => {
        if (isPluginIframe.value) return;
        console.log('WebSocket 連接已關閉，嘗試重連...');
        setTimeout(connectWebSocket, 5000);
      };

      websocket.value.onerror = (error) => {
        if (isPluginIframe.value) return;
        console.error('WebSocket 錯誤:', error);
      };
    };

    const fetchCurrentRaceData = async (opts = {}) => {
      if (!currentRace.value?.uid) {
        console.warn('當前沒有選中的場次');
        return;
      }
      const rid = String(currentRace.value.uid);
      const seq = ++refreshSeq;
      try {
        const response = await axios.get(buildGetviewUrl(rid, {
          advanceSeq: !!opts.advanceSeq,
        }));
        if (seq !== refreshSeq) return;
        if (response.data.code === -1) {
          console.warn('API返回錯誤碼 -1');
          data.value = [];
          current_minute.value = -1;
          entry_minute.value = null;
          time_points.value = [];
        } else {
          afterTestLiveFetch(response.data);
          if (mockLiveActive.value && opts.advanceSeq) {
            logMockLive('advance', {
              test_seq: testLiveSession.seq,
              test_entry_min: testLiveSession.entryMin,
              snapshot: horseChartRef.value?.getTimelineDebugSnapshot?.(),
            });
          } else if (mockLiveActive.value) {
            logMockLive('refresh', {
              test_seq: testLiveSession.seq,
              snapshot: horseChartRef.value?.getTimelineDebugSnapshot?.(),
            });
          }
          await applyViewPayload(response.data, rid);
        }
      } catch (error) {
        if (seq === refreshSeq) {
          console.error('獲取當前場次數據失敗:', error);
        }
      }
    };

    onMounted(() => {
      document.documentElement.classList.add('tyq-mobile-root');
      let vpMeta = document.querySelector('meta[name="viewport"]');
      if (!vpMeta) {
        vpMeta = document.createElement('meta');
        vpMeta.setAttribute('name', 'viewport');
        document.head.appendChild(vpMeta);
      }
      vpMeta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
      );
      if (isPluginIframe.value) {
        document.documentElement.classList.add('tyq-plugin-iframe-root');
      }
      clearStaleMockLiveFlag();
      publishTyqVersion();
      printTimelineDebugHint();
      printMockLiveHint();
      if (mockLiveActive.value) {
        window.tyqTimelineMock = {
          refresh: () => mockSimulateRefresh(false),
          advance: () => mockSimulateRefresh(true),
          snapLive: () => mockSnapLiveEdge(),
          startAuto: (ms = 5000) => {
            stopMockAuto();
            mockAutoTimer = setInterval(() => mockSimulateRefresh(true), ms);
            mockAutoRunning.value = true;
          },
          stopAuto: () => stopMockAuto(),
          getState: () => ({
            test_seq: testLiveSession.seq,
            test_entry_min: testLiveSession.entryMin,
            ...horseChartRef.value?.getTimelineDebugSnapshot?.(),
          }),
        };
        console.info('[tyq] window.tyqTimelineMock 已就绪');
      }
      window.addEventListener('message', onPluginMessage);
      console.log('組件已掛載', {
        plugin: isPluginIframe.value,
        mobile: true,
        w: window.innerWidth,
      });
      
      // 添加 URL 参数变化监听
      window.addEventListener('popstate', () => {
        const urlRaceId = getUrlParam('race_id');
        if (urlRaceId && races.value.length > 0) {
          const targetRace = races.value.find(race => String(race.uid) === String(urlRaceId));
          if (targetRace) {
            selectedRace.value = targetRace.sn;
            currentRace.value = targetRace;
            fetchRaceData(targetRace.uid);
          }
        }
      });
      
      // 確保場次選擇器可見
      setTimeout(() => {
        const raceSelector = document.querySelector('.race-selector');
        if (raceSelector) {
          console.log('場次選擇器存在');
          raceSelector.style.display = 'block';
          raceSelector.style.visibility = 'visible';
          raceSelector.style.zIndex = '1000';
        } else {
          console.warn('場次選擇器不存在');
        }
      }, 500);
      
      const urlRaceId = getUrlParam('race_id');
      fetchRaces().then(() => {
        if (isPluginIframe.value && urlRaceId && !data.value.length) {
          schedulePluginBootstrap();
        }
      });
      connectWebSocket();
      window.addEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
        window.visualViewport.addEventListener('scroll', handleResize);
      }
      setTimeout(updateMobileLayout, 300);
      setTimeout(updateMobileLayout, 1000);
      
      // 檢查組件是否正確渲染
      setTimeout(() => {

        // 如果圖表元素不存在，嘗試強制刷新數據
        if (!document.querySelector('.echarts-chart') && currentRace.value) {
          console.log('圖表元素不存在，嘗試重新獲取數據');
          fetchRaceData(currentRace.value.uid);
        }
      }, 1500);
    });

    onUnmounted(() => {
      cancelPluginBootstrap();
      stopMockAuto();
      if (window.tyqTimelineMock) delete window.tyqTimelineMock;
      window.removeEventListener('message', onPluginMessage);
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
      // 移除 URL 参数变化监听
      window.removeEventListener('popstate', () => {});
      if (websocket.value) {
        websocket.value.close();
      }
    });

    const showMore = ref(false)
    const viewMore = () => {
      showMore.value = !showMore.value
    }

    return {
      data,
      current_minute,
      entry_minute,
      time_points,
      view_source,
      raceCompleted,
      selectedRace,
      races,
      currentGame,
      selectRace,
      currentRace,
      formatDateTime,
      tableWidthValue,
      tableHeightValue,
      mockSnapLiveEdge,
      mockSimulateRefresh,
      mockToggleAuto,
      mockAutoRunning,
      mockSyncDisplay,
      mockViewDisplay,
      testEntryDisplay,
      testLiveSession,
      showMockLivePanel,
      viewMore,
      showMore,
      isPluginIframe,
      pluginStatus,
      raceDataLoading,
      horseChartRef,
      highlightPicks,
      highlightBankers,
      highlightColdMut00,
      highlightWarmMut00,
      highlightHotMut00,
      tableSortMode,
      chartSortMode,
      onSpectrumSortChange,
      tableViewMinute,
      tableOvernightActive,
      onChartViewMinuteChange,
      appVersion,
    };
  }
}
</script>

<style>
/* 移除所有容器邊框 */
.container {
  border: none;
}

.nav-bar {
  border: none;
}

.race-selector {
  border: none;
}

.view-data-container {
  border: 1px solid blue; /* 添加邊框以顯示表格容器邊界 */
}

.container:not(.tyq-mobile-layout) .chart-data-container {
  width: 100%;
  height: 35vh;
  overflow: hidden;
  padding: 0;
  margin: 0;
  position: absolute;
  left: 0;
  right: 0;
  box-sizing: border-box;
  border: 1px solid red;
}

.content-wrapper {
  border-bottom: 1px solid rgb(145, 145, 145); /* 添加邊框以顯示內容包裝器邊界 */
}

.table-wrapper {
  border: none;
}

.chart-wrapper {
  border: none;
}

/* 導航條樣式 */
.nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;  /* 減小高度 */
  background-color: #2c2c2c;
  color: white;
  z-index: 1000;
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 16px;
}

.nav-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.logo-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.logo {
  width: 24px;  /* 稍微缩小 logo */
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-text {
  font-size: 9px;  /* 稍微缩小文字 */
  color: #FFD700;
  margin-top: -2px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.nav-title {
  font-size: 14px;  /* 稍微缩小標題 */
  font-weight: bold;
}

/* 右上角版本号 */
.app-version {
  font-size: 11px;
  color: #FFD700;
  letter-spacing: 0.3px;
  white-space: nowrap;
  user-select: none;
}

/* 調整容器位置樣式（旧版非 tyq-mobile-layout 仍保留 68px） */
.container:not(.tyq-mobile-layout) {
  padding-top: 68px; /* 32px(導航條) + 36px(選擇器) */
}

.container:not(.tyq-mobile-layout) .view-data-container {
  width: 100%;
  height: 30vh;
  overflow: hidden;
  padding: 0;
  margin: 0;
  position: absolute;
  top: 68px;
  left: 0;
  right: 0;
  box-sizing: border-box;
}

.container:not(.tyq-mobile-layout) .content-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.container:not(.tyq-mobile-layout) .table-wrapper,
.container:not(.tyq-mobile-layout) .chart-wrapper {
  position: absolute;
  width: 100%;
  transform-origin: left top;
  border: 2px solid #333;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 場次選擇器樣式 */
.race-selector {
  position: fixed;
  top: 32px; /* 導航條高度 */
  left: 0;
  right: 0;
  height: 36px;
  display: flex !important; /* 強制顯示 */
  align-items: center;
  padding-left: 16px;
  background-color: #f5f5f5;
  z-index: 999;
  visibility: visible !important; /* 強制可見 */
  border: 2px solid rgb(255, 255, 255) !important; /* 添加邊框以便調試 */
}

.race-numbers {
  display: flex;
  gap: 2px;
  flex-wrap: wrap; /* 允許換行 */
}

.race-number {
  width: 22px;
  height: 22px;
  display: flex !important; /* 強制顯示 */
  align-items: center;
  justify-content: center;
  border: 1px solid #ccc;
  background-color: white;
  cursor: pointer;
  font-size: 12px;
  user-select: none;
  transition: all 0.2s;
  margin: 2px; /* 添加外邊距 */
  visibility: visible !important; /* 強制可見 */
}

.race-number:hover {
  border-color: #FFD700;
  color: #FFD700;
}

.race-number.active {
  background-color: #2c2c2c;
  color: #FFD700;
  border-color: #FFD700;
}

/* 調整表格位置，為選擇器留出空間 */
.table-wrapper {
  margin-top: 48px;
}

.app-detail{
  display: none !important;
}

/* 移動端適配 */
@media screen and (max-width: 430px) {
  .app-detail{
    display: block !important;
    text-align: center;
    align-content: center;
  }

  .view-data-container,
  .chart-data-container {
    border-width: 2px;
  }
  
  .content-wrapper {
    border-width: 1px;
  }
  
  .table-wrapper,
  .chart-wrapper {
    border-width: 1px;
  }
  
  .nav-title {
    font-size: 12px;
  }
  
  .app-version {
    font-size: 10px;
  }

  .race-number {
    width: 22px;
    height: 22px;
    font-size: 11px;
  }
  
  .race-selector {
    height: auto;
    min-height: 36px;
    flex-wrap: wrap;
    padding: 5px;
  }
  
  .race-numbers {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  
  .container:not(.tyq-mobile-layout) {
    padding-top: 64px; /* 32px + 32px */
  }
  
  .container:not(.tyq-mobile-layout) .view-data-container {
    top: 64px;
  }
  
  .chart-data-container {
    /* top: calc(30vh + 64px); */
    /* top: 500px; */
  }
}


/* iPad Air 特定樣式 */
@media screen and (width: 820px) and (height: 1180px) {
  .content-wrapper {
    height: 100%;
  }
  
  .table-wrapper,
  .chart-wrapper {
    height: auto;
  }
}

/* 修改表格標題樣式 */
.table-title {
  padding: 2px 8px;
  font-size: 12px;
  color: #f6f2f2; /* 加深顏色以提高清晰度 */
  display: flex;
  gap: 8px;
  align-items: center;
  height: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; /* 使用系統字體提高清晰度 */
  letter-spacing: 0.2px; /* 稍微增加字間距提高可讀性 */
}

.table-title span {
  white-space: nowrap;
  font-weight: 500; /* 稍微加粗以提高清晰度 */
}

.place {
  display: flex;
  align-items: center;
  gap: 2px;
}

.place-circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid #666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

/* 舊版移動端（僅非 tyq-mobile-layout） */
@media screen and (max-width: 1279px) {
  .container:not(.tyq-mobile-layout) .view-data-container {
    left: 0;
    right: 0;
    bottom: 25vh;
    overflow: auto;
  }
}

/* PC端四分屏布局（僅非 tyq-mobile-layout） */
@media screen and (min-width: 1024px) {
  .container:not(.tyq-mobile-layout) .view-data-container {
    width: 50%;
    height: calc(50vh + 36px);
    left: 0;
    right: auto;
  }

  .container:not(.tyq-mobile-layout) .chart-data-container {
    width: 50%;
    height: calc(50vh + 36px);
    top: 68px;
    left: 50%;
    right: 0;
  }
}

/* PC端樣式 */
@media screen and (min-width: 1280px) {
  .container,
  .nav-bar,
  .race-selector,
  .view-data-container,
  .chart-data-container,
  .content-wrapper,
  .table-wrapper,
  .chart-wrapper {
    border: none;
  }

  .nav-bar {
    height: 64px;  /* 增加導航欄高度 */
    display: flex;
    align-items: center;
  }

  .logo {
    height: 40px;
    margin: 0 12px;
  }

  .title {
    font-size: 18px;
  }

  .race-selector {
    top: 64px;  /* 調整場次選擇器位置，緊跟導航欄 */
    height: 44px;
  }

  .container:not(.tyq-mobile-layout) .view-data-container {
    top: 108px;
    height: calc(60vh - 108px);
  }

  .container:not(.tyq-mobile-layout) .chart-data-container {
    top: 108px;
    left: 50%;
    width: 50%;
  }

  .container:not(.tyq-mobile-layout) .chart-wrapper {
    position: absolute;
    width: 100%;
    height: calc(100% - 36px); /* 為滑塊預留空間 */
    transform-origin: left top;
    border: 2px dashed green;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  .race-number {
    min-width: 32px;     /* 增加按鈕寬度 */
    height: 32px;        /* 增加按鈕高度 */
    margin: 0 4px;       /* 保持原有間距 */
    font-size: 16px;     /* 增加字體大小 */
    border: 1px solid #666;
    background-color: transparent;
  }

  .race-number.active {
    background-color: #333;
    border-color: #333;
  }

  .race-number:hover {
    border-color: #999;
  }

  .race-info-line {
    font-size: 16px;
    line-height: 1.4;
  }

  .race-info {
    gap: 4px;
  }
}

.race-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 12px;
}

.race-info-line {
  font-size: 14px;
  line-height: 1.2;
  color: #fff;
}

/* 添加新的樣式 */
.odds-display-container {
  width: 100%;
  height: 70px;
  position: absolute;
  top: calc(30vh + 68px + 35vh); /* 調整為新的圖表高度 */
  left: 0;
  background-color: #fff;
  z-index: 1000;
  border: 1px solid orange;
}

/* 添加調試信息樣式 */
.debug-info {
  color: red;
  font-size: 12px;
  margin-left: 10px;
}

/* 修改容器底色為白色（移动端频谱区滚动见下方 .tyq-mobile-layout 覆盖） */
.view-data-container, 
.chart-data-container {
  min-height: 200px;
  background-color: #ffffff; /* 改為白色 */
  border: 1px solid #e0e0e0; /* 使用淺灰色邊框替代紅色調試邊框 */
  z-index: 10;
  display: block !important;
  visibility: visible !important;
  overflow: visible;
}

.table-wrapper, 
.chart-wrapper {
  min-height: 100px;
  background-color: #ffffff; /* 改為白色 */
  border: 1px solid #e0e0e0; /* 使用淺灰色邊框替代藍色調試邊框 */
  display: block !important;
  visibility: visible !important;
  overflow: visible !important;
}

/* 確保圖表有明確的尺寸並且可見（PC 寬屏；窄屏由下方覆蓋為 100% 寬） */
.echarts-chart {
  min-width: 600px;
  min-height: 300px;
  width: 100%;
  box-sizing: border-box;
  border: none;
  display: block !important;
  visibility: visible !important;
}

/* 移動端 / 平板：柱圖必須跟容器同寬，禁止 600px 撐出屏幕 */
@media screen and (max-width: 1023px) {
  .chart-data-container {
    overflow: hidden !important;
  }
  .chart-data-container .content-wrapper,
  .chart-data-container .chart-wrapper,
  .chart-data-container .chart-container {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }
  .chart-data-container .chart-wrapper {
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    min-height: 0 !important;
  }
  .chart-data-container .echarts-chart,
  .chart-data-container .chart {
    min-width: 0 !important;
    min-height: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .chart-data-container .chart-container {
    min-height: 0 !important;
  }
}

/* 確保表格有明確的尺寸並且可見 */
.custom-table {
  min-width: 600px;
  border: none; /* 移除黃色調試邊框 */
  display: block !important;
  visibility: visible !important;
}

/* ========== 移动端统一布局（浏览器 + 插件浮窗同一套） ========== */
.container.tyq-mobile-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
  padding-top: 32px;
  overflow: hidden;
  background: #fff;
}
.container.tyq-mobile-layout.tyq-plugin-iframe {
  padding-top: 0;
  height: 100%;
}
.container.tyq-mobile-layout .plugin-session-bar {
  flex: 0 0 auto;
  padding: 4px 8px;
  background: #2c2c2c;
  color: #fff;
  font-size: 11px;
  line-height: 1.3;
}
.container.tyq-mobile-layout .plugin-session-line {
  display: block;
}
.container.tyq-mobile-layout .plugin-load-hint {
  flex: 0 0 auto;
  padding: 6px 10px;
  font-size: 12px;
  color: #c62828;
  background: #fff3e0;
}
.container.tyq-mobile-layout .main-pane {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  overflow: hidden;
}
.container.tyq-mobile-layout.tyq-plugin-iframe .main-pane {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.container.tyq-mobile-layout .race-selector {
  flex: 0 0 auto;
  position: relative !important;
  top: auto !important;
}
.container.tyq-mobile-layout.tyq-plugin-iframe .view-data-container {
  flex: 0 0 auto;
  height: auto !important;
  min-height: 0;
  max-height: none !important;
  overflow: visible !important;
  flex-shrink: 0;
}
.container.tyq-mobile-layout.tyq-plugin-iframe .view-data-container .content-wrapper,
.container.tyq-mobile-layout.tyq-plugin-iframe .view-data-container .table-wrapper,
.container.tyq-mobile-layout.tyq-plugin-iframe .view-data-container .table-container,
.container.tyq-mobile-layout.tyq-plugin-iframe .view-data-container .custom-table {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
}
.container.tyq-mobile-layout .view-data-container {
  flex: 1 1 0;
  min-height: 0 !important;
  position: relative !important;
  top: auto !important;
  bottom: auto !important;
  left: auto !important;
  right: auto !important;
  width: 100% !important;
  max-width: 100% !important;
  max-height: var(--tyq-spectrum-max, none);
  overflow-x: hidden !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  border: none;
  order: 1;
}
/* 浏览器移动端：限定高度内纵向滚动，14 马等可滑完；勿被全局 overflow:visible 盖掉 */
.container.tyq-mobile-layout:not(.tyq-plugin-iframe) .view-data-container {
  flex: 1 1 0;
  min-height: 0 !important;
  height: auto !important;
  max-height: var(--tyq-spectrum-max, none) !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}
.container.tyq-mobile-layout .view-data-container .content-wrapper {
  height: auto !important;
  width: 100% !important;
  overflow: visible !important;
}
.container.tyq-mobile-layout .table-wrapper {
  position: relative !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  width: 100% !important;
  max-width: 100% !important;
  margin-top: 0 !important;
  border: none;
}
.container.tyq-mobile-layout .table-container,
.container.tyq-mobile-layout .custom-table {
  min-width: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
}
.container.tyq-mobile-layout.tyq-plugin-iframe .chart-data-container {
  flex: 1 1 0;
  height: var(--tyq-block-h, 200px) !important;
  min-height: var(--tyq-block-h, 200px);
  max-height: var(--tyq-block-h, 200px);
  flex-shrink: 1;
  margin-top: 0 !important;
}
.container.tyq-mobile-layout .chart-data-container {
  flex: 0 0 auto;
  height: var(--tyq-block-h, 246px);
  min-height: var(--tyq-block-h, 246px);
  max-height: var(--tyq-block-h, 246px);
  position: relative !important;
  top: auto !important;
  bottom: auto !important;
  left: auto !important;
  right: auto !important;
  flex-shrink: 0;
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden;
  border: none;
  order: 2;
  margin-top: 0 !important;
  display: flex;
  flex-direction: column;
}
.container.tyq-mobile-layout .chart-data-container--mock-live .content-wrapper {
  flex: 1 1 0;
  min-height: 0;
}
.container.tyq-mobile-layout .chart-data-container--mock-live .tyq-mock-live-panel {
  flex: 0 0 var(--tyq-mock-panel-h, 56px);
  order: -1;
}
.container.tyq-mobile-layout .chart-data-container .content-wrapper,
.container.tyq-mobile-layout .chart-wrapper {
  height: 100% !important;
  width: 100% !important;
  position: relative !important;
  transform: none !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden;
  border: none;
}
.container.tyq-mobile-layout .chart-container {
  flex: 0 0 auto;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}
.container.tyq-mobile-layout .echarts-chart,
.container.tyq-mobile-layout .chart {
  flex: 0 0 var(--tyq-chart-h, 206px);
  width: 100% !important;
  min-width: 0 !important;
  height: var(--tyq-chart-h, 206px) !important;
  min-height: var(--tyq-chart-h, 206px) !important;
  max-height: var(--tyq-chart-h, 206px) !important;
  display: block !important;
  box-sizing: border-box;
}
.container.tyq-mobile-layout.tyq-plugin-iframe .chart-container--db-bubbles .chart,
.container.tyq-mobile-layout.tyq-plugin-iframe .chart-container--db-bubbles .echarts-chart {
  flex: 0 0 var(--tyq-chart-h, 186px) !important;
  height: var(--tyq-chart-h, 186px) !important;
  min-height: var(--tyq-chart-h, 186px) !important;
  max-height: var(--tyq-chart-h, 186px) !important;
}
.container.tyq-mobile-layout .chart-db-bubbles {
  flex: 0 0 var(--tyq-db-bubble-h, 36px);
  min-height: var(--tyq-db-bubble-h, 36px);
  max-height: var(--tyq-db-bubble-h, 36px);
  margin-top: var(--tyq-chart-bubble-gap, 1px);
}
.container.tyq-mobile-layout .slider-container {
  flex: 0 0 var(--tyq-slider-h, 40px);
  height: var(--tyq-slider-h, 40px);
  min-height: var(--tyq-slider-h, 40px);
  max-height: var(--tyq-slider-h, 40px);
  width: 100%;
  background: #fff;
  z-index: 5;
  flex-shrink: 0;
  overflow: visible;
  border-top: 1px solid #eee;
}
html.tyq-plugin-iframe-root,
html.tyq-plugin-iframe-root body,
html.tyq-plugin-iframe-root #app,
html.tyq-mobile-root,
html.tyq-mobile-root body,
html.tyq-mobile-root #app {
  margin: 0 !important;
  padding: 0 !important;
  height: 100% !important;
  width: 100% !important;
  max-width: 100vw !important;
  overflow-x: hidden !important;
  box-sizing: border-box !important;
}

.tyq-mock-live-panel {
  flex: 0 0 auto;
  width: 100%;
  padding: 4px 6px;
  background: rgba(20, 30, 48, 0.94);
  color: #e3f2fd;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 10px;
  line-height: 1.25;
  box-sizing: border-box;
  pointer-events: auto;
  z-index: 6;
}
.tyq-mock-live-title {
  margin-bottom: 4px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tyq-mock-live-btns {
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
}
.tyq-mock-live-btns button {
  flex: 1 1 0;
  min-width: 0;
  padding: 5px 4px;
  border: none;
  border-radius: 4px;
  background: #1565c0;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
}
.tyq-mock-live-btns button:active {
  background: #0d47a1;
}
</style>